/**
 * `SettingsPresenter` — the panel behind the HUD's gear.
 *
 * Decomp read for this file:
 *   bustraffic_kit/code/decomp_game/GamePlayUIModel$$OnSettingsButtonClicked__0x21462b0.c
 *   bustraffic_kit/code/decomp_game/SettingsPresenter$$Initialize__0x213ff00.c
 *   bustraffic_kit/code/decomp_game/SettingsPresenter$$SoundButtonClick__0x21401ec.c
 *   bustraffic_kit/code/decomp_game/SettingsModel$$OnSoundButtonClicked__0x213fbfc.c
 *   bustraffic_kit/code/decomp_game/SettingsModel$$OnHapticButtonClicked__0x213fc90.c
 *   bustraffic_kit/code/decomp_game/SettingsModel$$OnCloseButtonClicked__0x213fddc.c
 *   bustraffic_kit/code/decomp_game/SettingsAnimations$$Show__0x213f444.c
 *   bustraffic_kit/code/decomp_game/SettingsAnimations$$Hide__0x213f72c.c
 *   bustraffic_kit/code/decomp_game/SettingsAnimations$$ToggleSoundButton__0x213f2a0.c
 *   .../Assets/Resources/popups/SettingsPresenter.prefab
 *
 * ## What it does to the game
 *
 * 🔍 `SettingsPresenter.Initialize` opens with
 * `EventBus.Publish(new TimeScaleChangedEvent(0f))` and
 * `SettingsModel.OnCloseButtonClicked` publishes `TimeScaleChangedEvent(1f)`
 * before unloading — so the board is **paused** while the panel is up. The
 * popup's own tweens run on `SetUpdate(true)`, which is why they still move.
 *
 *     OnSoundButtonClicked:   if (_disableButtonClicks) return;
 *                             SoundService.ToggleSound(); Taptic.Medium();
 *                             SoundService.ExecutePlay(ButtonClick)
 *     OnHapticButtonClicked:  same shape with ToggleHaptic
 *     OnCloseButtonClicked:   Play(ButtonClick); TimeScale(1); PopupService.Close
 *
 * ⚠️ **DELIBERATE DEVIATION (user's request, 2026-09-08): the `Privacy`
 * button is not built.** `SettingsModel.OnPrivacyPolicyButtonClicked`
 * (`__0x213fd24.c`) is `Play(ButtonClick); Application.OpenURL(<literal>)`, and
 * that literal did not survive IL2CPP — so the button only played a click and
 * went nowhere. The port drops the button, the `Divider` above it and the
 * `Shield` sprite. The geometry below is kept so it can be rebuilt if a real
 * URL ever turns up.
 *
 * ## The switches
 *
 * 🔍 `SettingsAnimations.ToggleSoundButton(on, instant)` / `ToggleHapticButton`:
 *
 *     toggleText.text = on ? <literal A> : <literal B>        // 📐 "ON" / "OFF"
 *     buttonImage.sprite = on ? buttonOnSprite : buttonOffSprite
 *     x = on ? +40 : -40
 *     instant ? slider.anchoredPosition.x = x : slider.DOAnchorPosX(x, 0.2f)
 *
 * `Initialize(haptic, sound)` calls both with `instant = true`, so the panel
 * opens with the knobs already in place. 📐 `buttonOnSprite` / `buttonOffSprite`
 * are `[SerializeField]`s and gone; the screenshot's green ON knob is
 * `btn_1_green`, and `btn_1_red` is the same button in red, which is what a
 * shipped "off" would be — ❓ only that half is inferred.
 *
 * ## The animations
 *
 * 🔍 `Show` is `FailAnimations.Show`'s shape: background fade 0.2, frame fade
 * 0.3, frame `DOScale(1, 0.4)`. `Hide` differs from the base: frame fade 0.15,
 * background fade 0.2, frame `DOScale(0.95f, 0.25f)` — it shrinks a little, it
 * does not collapse.
 *
 * ## Geometry — the prefab, in CanvasScaler units
 *
 *     Body/BackgroundImage     centred, 538 x 557.4, y -70          Panel_Yellow(Soft)
 *                              ⚠️ built 538 x 427.7 — see below
 *     Contents                 centred, 494 x 471.7, y -80
 *                              ⚠️ built 494 x 342 — see below
 *       Title                  top-centre, pivot bottom, y +10, 365.5 x 90   Panel_Title_Blue
 *         TitleText            bottom-stretch, height 76.6, y +5, inset 25
 *       BackgroundImage        stretch, sizeDelta y -60             Panel_Inside_Yellow(Soft)
 *       CloseButtonBG          top-right, (-17, +21), 75 square      btn_2_red
 *         CrossImage           stretch -38 / -38.1, y +1.9           Exit
 *       OptionsGroup           stretch, y -35, sizeDelta y -70 (VerticalLayoutGroup)
 *         Sound / Haptic       LayoutElement — heights stripped
 *           *Text              left-middle, x 156.56, 185.62 x 50
 *           *Icon              left-middle, (22.5, 3.6), 45 square  icon_Music / icon_Vibrate
 *           *Button            right-middle, x -93, 178.52 x 72     Panel_Toggle_Yellow(Soft)
 *             ButtonSlider     centred, 112 x 78.3, x = ±40
 *               ButtonImage    stretch                              btn_1_green / btn_1_red
 *               ToggleText     centred, 73.8 x 41.2
 *         Divider              Image + LayoutElement                LineVertical
 *         (Divider)            ⛔ dropped with `Privacy`
 *         (Buttons/Privacy)    ⛔ dropped — 150 x 86, btn_1_base2 + btn_1_blue;
 *                              ButtonIcon (35, 0.4) 35 square Shield,
 *                              ButtonText (18.5, 1.71) 72 x 42.52
 *     Version_Number           bottom-stretch, pivot bottom, y +32, height 44.97, inset 100
 *
 * ❓ Every `LayoutElement.preferredHeight` and the `VerticalLayoutGroup`'s
 * spacing went with their components, so the **row heights are measured off
 * the screenshot** — 82 for a switch row, 38 for a divider — and marked
 * PLACEHOLDER in `index.html`. `Support`, `Restore`, `Home` are `m_IsActive: 0`;
 * `Privacy` is `m_IsActive: 1` in the prefab but is dropped (the ⚠️ note above).
 *
 * ⚠️ **And the panel was resized to match (user's request, 2026-09-08).** The
 * prefab's `Contents` is 471.7 tall because it carried the switches *and* the
 * button; with the button gone it left 199.7 u of empty cream. The port builds
 * `Contents` at **342** = 70 (the prefab's own inset above the first row) + 82
 * + 38 + 82 + 70 (that inset mirrored below the last row), and the frame at
 * **427.7** = 342 + the prefab's margins around `Contents` (32.85 above, 52.85
 * below), which are left alone. So the title still dips 32.85 into the rim and
 * `Version_Number` keeps its band; only the empty stretch is gone.
 *
 * Every sprite above is a `Sprite/*.asset` (📐 screenshot; `settingsPopup.test.ts`).
 */
import { POPUP_ANIM, Popup, el, sprite } from './popups'

/** 🔍 `SettingsAnimations`. */
export const SETTINGS_ANIM = {
  /** `Show`: the same 0.2 / 0.3 / 0.4 as `FailAnimations`. */
  showBackgroundFade: POPUP_ANIM.showFadeSeconds,
  showFrameFade: POPUP_ANIM.failFrameFadeSeconds,
  showFrameScale: POPUP_ANIM.failFrameScaleSeconds,
  /** `Hide`: frame `DOFade(0, 0.15f)`, background `DOFade(0, 0.2f)`, frame `DOScale(0.95f, 0.25f)`. */
  hideFrameFade: 0.15,
  hideBackgroundFade: 0.2,
  hideFrameScale: 0.25,
  hideFrameScaleTo: 0.95,
  /** `ToggleSoundButton`: `DOAnchorPosX(±40, 0.2f)`. */
  toggleSlideX: 40,
  toggleSlide: 0.2,
}

/** 📐 Read off the shipped panel; `version` is the port's own. */
export const SETTINGS_TEXT = {
  title: 'SETTINGS',
  sound: 'Sound',
  haptic: 'Vibration',
  on: 'ON',
  off: 'OFF',
  /** `Version_Number` — `"Version: " + Application.version` (📐 the format). */
  version: (v: string) => `Version: ${v}`,
}

declare const __APP_VERSION__: string | undefined
/** `Application.version` — the port's `package.json` version, injected by Vite. */
export const APP_VERSION = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'dev'

export interface SettingsHandlers {
  /** `SettingsModel.OnSoundButtonClicked` — returns the new `IsSoundEnabled`. */
  onToggleSound(): boolean
  /** `SettingsModel.OnHapticButtonClicked` — returns the new `IsHapticEnabled`. */
  onToggleHaptic(): boolean
  /** `SettingsModel.OnCloseButtonClicked` — the caller lifts the pause. */
  onCloseSettings(): void
}

export interface SettingsState {
  sound: boolean
  haptic: boolean
}

/** One `SoundButton` / `HapticButton`: the track, the sliding knob, its caption. */
class ToggleButton {
  readonly root: HTMLButtonElement
  private readonly slider: HTMLElement
  private readonly text: HTMLElement

  constructor(onClick: () => void) {
    this.text = el('span', 'toggleText')
    this.slider = el('div', 'toggleSlider', el('div', 'toggleKnob', this.text))
    this.root = el('button', 'toggleButton', this.slider) as HTMLButtonElement
    this.root.type = 'button'
    this.root.addEventListener('click', onClick)
  }

  /** `ToggleSoundButton(on, instant)`. */
  set(on: boolean, instant: boolean): void {
    this.text.textContent = on ? SETTINGS_TEXT.on : SETTINGS_TEXT.off
    this.root.classList.toggle('on', on)
    this.root.classList.toggle('instant', instant)
    this.root.setAttribute('aria-pressed', String(on))
  }
}

export class SettingsPopup extends Popup {
  private readonly sound: ToggleButton
  private readonly haptic: ToggleButton

  constructor(parent: HTMLElement, handlers: SettingsHandlers) {
    super(parent, 'settingsPopup')

    this.sound = new ToggleButton(() => this.sound.set(handlers.onToggleSound(), false))
    this.haptic = new ToggleButton(() => this.haptic.set(handlers.onToggleHaptic(), false))

    const close = el('button', 'popupClose', sprite('popupCloseIcon', 'Exit.png')) as HTMLButtonElement
    close.type = 'button'
    close.setAttribute('aria-label', 'close')
    close.addEventListener('click', () => {
      this.close()
      handlers.onCloseSettings()
    })

    const row = (cls: string, icon: string, label: string, toggle: ToggleButton) =>
      el('div', `settingsRow ${cls}`, sprite('rowIcon', icon), el('span', 'rowText', label), toggle.root)

    this.frame.append(
      el(
        'div',
        'settingsContents',
        el('div', 'settingsInner'),
        el('div', 'settingsTitle', el('span', 'settingsTitleText', SETTINGS_TEXT.title)),
        close,
        el(
          'div',
          'optionsGroup',
          row('soundRow', 'icon_Music.png', SETTINGS_TEXT.sound, this.sound),
          el('div', 'divider', sprite('dividerLine', 'LineVertical.png')),
          row('hapticRow', 'icon_Vibrate.png', SETTINGS_TEXT.haptic, this.haptic),
        ),
      ),
      el('div', 'versionText', SETTINGS_TEXT.version(APP_VERSION)),
    )
  }

  /** `SettingsPresenter.Initialize` → `SettingsAnimations.Initialize(haptic, sound)` → `Open`. */
  show(state: SettingsState): void {
    this.sound.set(state.sound, true)
    this.haptic.set(state.haptic, true)
    this.open()
  }
}
